<template>
  <base-hm-input :id="id"
                 :class="[
                   $attrs['class'],
                   { 'has-inline-icon-left': hasSlot('icon-inline-left') },
                   'has-inline-icon-right',
                   { 'component__hm-input-rate--inline': inline },
                 ]"
                 :label="label"
                 :help="help"
                 :has-focus="hasFocus"
                 :is-empty="isValueEmpty"
                 :parent-errors="errors"
                 :name="name"
                 :cols="cols"
                 :hidden="hidden"
                 :style="iconCountStyleVariables"
                 :disabled="disabledCombined">
    <input v-if="lazy"
           :id="`${id}-input`"
           v-model.trim.lazy="inputValue"
           :data-vv-as="label"
           :data-vv-name="name"
           :disabled="disabledCombined"
           :class="[ { 'hm-input__input hm-input__input--rate--rate-mode': rateMode } ]"
           class="hm-input__input hm-input__input--rate"
           type="text"
           @focus="onFocus"
           @blur="onBlur">
    <input v-else
           :id="`${id}-input`"
           v-model.trim="inputValue"
           :data-vv-as="label"
           :data-vv-name="name"
           :disabled="disabledCombined"
           :class="[ { 'hm-input__input hm-input__input--rate--rate-mode': rateMode } ]"
           class="hm-input__input hm-input__input--rate"
           type="text"
           @focus="onFocus"
           @blur="onBlur">
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}, {'hm-input__icon__wrapper--left--fixed': !relative || !absolute }]" class="hm-input__icon__wrapper hm-input__icon__wrapper--left">
      <template v-if="relative">
        <input-icon-button v-if="internalValue.relative"
                           :disabled="disabledCombined"
                           :border="false"
                           :is-slot="relative && absolute"
                           icon="plus"
                           @click="toggleRelative"/>
        <input-icon-button v-else
                           :disabled="disabledCombined"
                           :border="false"
                           :is-slot="relative && absolute"
                           icon="equals"
                           @click="toggleRelative"/>
      </template>
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-left">
      </slot>
    </div>
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}, {'hm-input__icon__wrapper--right--rate-mode': rateMode }]" class="hm-input__icon__wrapper hm-input__icon__wrapper--right">
      <div :class="['hm-input__input__currency', {'hm-input__input__currency--slot': percentage}, {'hm-input__input__currency--disabled': !percentage}]" @click="toggleCurrency" v-html="displayCurrency"></div>
      <template v-if="rateMode">
        <span style="line-height:3.6rem;height:3.2rem;font-size:2rem;width:1.6rem;text-align:center;">/</span>
        <input-icon-button v-if="internalValue.rateMode === 'PerRoom'"
                           :border="false"
                           :disabled="disabledCombined || rateModeDisabled"
                           :is-slot="rateMode && !rateModeDisabled"
                           icon="per-room"
                           @click="toggleRateMode" />
        <input-icon-button v-else
                           :border="false"
                           :disabled="disabledCombined || rateModeDisabled"
                           :is-slot="rateMode && !rateModeDisabled"
                           icon="per-person"
                           @click="toggleRateMode" />
      </template>
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-right">
      </slot>
    </div>
  </base-hm-input>
</template>

<script>

import { isNil, trim, isArray, isFinite, isEmpty, sum } from 'lodash';
import HmInputMixin from '@/mixins/HmInputMixin';
import parseDecimalNumber from 'parse-decimal-number';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import { helpers } from 'vuelidate/lib/validators';

export default {
  name: 'HmInputRate',

  mixins: [HmInputMixin, HotelmeisterMixin],

  props: {
    currency: {
      required: false,
      type: String,
      default: null,
    },
    inline: {
      required: false,
      type: Boolean,
      default: false,
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    lazy: {
      required: false,
      type: Boolean,
      default: false,
    },
    percentage: {
      required: false,
      type: Boolean,
      default: true,
    },
    absolute: {
      required: false,
      type: Boolean,
      default: true,
    },
    relative: {
      required: false,
      type: Boolean,
      default: false,
    },
    rateMode: {
      required: false,
      type: Boolean,
      default: false,
    },
    rateModeDisabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    name: {
      required: false,
      type: String,
      default: 'value',
    },
  },

  data: function () {
    return {
      hover: false,
      inputValue: '',
      internalValue: {
        relative: false,
        amount: null,
        percentage: false,
        rateMode: 'PerRoom',
      },
    };
  },

  validations: function () {
    return {
      [this.name]: {
        custom: helpers.withParams(
          { message: this.$t('ValidationError.rate_negative', { field: this.label }) },
          (_, $vm) => {
            if (!$vm.internalValue.relative && $vm.internalValue.amount < 0) return false;
            if ($vm.internalValue.relative && $vm.internalValue.amount < -100) return false;
            else return true;
          },
        ),
      },
    };
  },

  computed: {
    defaultValue: function () {
      return this.required ? 0 : null;
    },
    displayCurrency: function () {
      if (this.percentage === true && this.internalValue.percentage === true) {
        return '%';
      } else {
        return this.currency || this.hotelSettings.details.currency;
      }
    },
    iconCountLeft: function () {
      const slot = this.getSlot('icon-inline-left');

      if (isNil(slot)) return 0 + (this.relative ? 1 : 0);
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length + (this.relative ? 1 : 0);
      else return 1 + (this.relative ? 1 : 0);
    },
    iconCountRight: function () {
      const slot = this.getSlot('icon-inline-right');

      if (isNil(slot)) return 0 + 1 + (this.rateMode ? 2 : 0);
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length + 1 + (this.rateMode ? 2 : 0);
      else return 1 + 1 + (this.rateMode ? 2 : 0);
    },
    iconCountStyleVariables: function () {
      return {
        '--icons-left': this.iconCountLeft,
        '--icons-right': this.iconCountRight,
      };
    },
  },

  watch: {
    internalValue: function (newVal) {
      this.inputValue = isNil(this.internalValue.amount) || !isFinite(this.internalValue.amount) ? this.defaultValue : `${this.internalValue.amount.toLocaleString(
        this.$i18n.locale,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
        },
      )}`;
    },
    // TODO: update on locale change
  },

  created: function () {
    this.internalValue = {
      percentage: false,
      ...this.value,
    };

    if (this.rateMode) {
      this.internalValue = {
        rateMode: 'PerRoom',
        ...this.value,
      };
    }

    if (isNil(this.internalValue.percentage)) {
      this.internalValue.percentage = false;
    }

    if (this.absolute === false) {
      this.internalValue.relative = true;
    }

    this.inputValue = isNil(this.internalValue.amount) || !isFinite(this.internalValue.amount) ? this.defaultValue : `${this.internalValue.amount.toLocaleString(
      this.$i18n.locale,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 5,
      },
    )}`;
  },

  mounted: function () {
    this.$on('focus', () => {
      this.$el.querySelector('input').select();
    });

    this.$on('blur', () => {
      const options = {
        thousands: (9999).toLocaleString(this.$i18n.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[^\D]/g, ''),
        decimal: (0.1).toLocaleString(this.$i18n.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/[^\D]/g, ''),
      };

      const tempValue = isNil(this.inputValue) ? this.defaultValue : parseDecimalNumber(this.inputValue, options);

      this.internalValue.amount = isNil(tempValue) || !isFinite(tempValue) ? this.defaultValue : tempValue;

      this.inputValue = isNil(this.internalValue.amount) || !isFinite(this.internalValue.amount) ? this.defaultValue : `${this.internalValue.amount.toLocaleString(
        this.$i18n.locale,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
        },
      )}`;
    });
  },

  methods: {
    toggleCurrency() {
      if (this.percentage === true) {
        this.internalValue.percentage = !this.internalValue.percentage;
      }
    },
    toggleRelative() {
      if (this.relative === true && this.absolute === true) {
        this.internalValue.relative = !this.internalValue.relative;
      }
    },
    toggleRateMode() {
      if (this.rateMode === true) {
        if (this.internalValue.rateMode === 'PerRoom') {
          this.internalValue.rateMode = 'PerPerson';
        } else {
          this.internalValue.rateMode = 'PerRoom';
        }
      }
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
   @import '~styles/components/lib/HmInput';
   @import '~styles/import';

   .component__hm-input-rate {
     .inline-button {
       width: 3.2rem;
     }

     .hm-input:not(.hm-input--disabled) {
       .inline-button {
         @include icon-color($middlegray);
         @include icon-hover($text);
         &:hover {
           cursor: pointer;
         }
       }

       .hm-input__icon__wrapper--left {
         pointer-events: all;

         &--fixed {
           pointer-events: none;
         }
       }

       .hm-input__input__currency {
         pointer-events: all;
         cursor: pointer;

         &--disabled {
           pointer-events: none!important;
         }

         &--slot {
           position: relative;
           &:before {
             content: ' ';
             display: block;
             width: 100%!important;
             height: 100%!important;
             top: 0.0rem!important;
             left: 0.6rem;
             position: absolute;
             background-repeat: no-repeat;
             background-size: contain;
             background-position: 0 0;
             background-image: url('~svg/switch.svg');
             pointer-events: none;
           }
         }
       }
     }
   }

</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmInputRate.vue